import './erp-sidebar.js';
import './erp-header.js';
import type { ErpSidebar } from './erp-sidebar.js';
import type { ErpHeader } from './erp-header.js';

/**
 * `<erp-shell>` — layout raiz da área autenticada do ERP: sidebar fixa
 * + header + área de conteúdo onde o roteador (`core/router.ts`) monta
 * a página atual. Composição pura de `erp-sidebar` + `erp-header` +
 * `<main>` — não conhece rotas nem dado de negócio; expõe `sidebar`,
 * `header` e `outlet` para quem monta a aplicação (`main.ts`) conectar
 * o resto.
 */
export class ErpShell extends HTMLElement {
  private readonly root: ShadowRoot;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    this.root.innerHTML = `
      <style>
        :host {
          display: grid;
          grid-template-columns: 260px 1fr;
          grid-template-rows: 100%;
          height: 100vh;
          background: var(--pg-color-bg-base, #121417);
        }
        .area {
          display: flex;
          flex-direction: column;
          min-width: 0;
          height: 100vh;
        }
        main {
          flex: 1;
          overflow-y: auto;
          padding: var(--pg-space-6, 32px);
        }
        /* Sidebar vira trilho só-ícone em telas estreitas (mesmo breakpoint
           já usado em dashboard-tecnico.page.ts/clientes.page.ts) — nunca
           menu overlay/hamburguer, que exigiria novo estado de UI. */
        @media (max-width: 900px) {
          :host { grid-template-columns: 64px 1fr; }
          main { padding: var(--pg-space-4, 16px); }
        }
      </style>
      <erp-sidebar part="sidebar"></erp-sidebar>
      <div class="area">
        <erp-header part="header"></erp-header>
        <main id="conteudo" part="conteudo"></main>
      </div>
    `;
  }

  get sidebar(): ErpSidebar {
    // `querySelector` é tipado via `HTMLElementTagNameMap` (declarado em
    // `erp-sidebar.ts`); o `!` é seguro porque o elemento é criado no
    // `innerHTML` fixo do construtor, sempre presente.
    return this.root.querySelector('erp-sidebar')!;
  }

  get header(): ErpHeader {
    return this.root.querySelector('erp-header')!;
  }

  get outlet(): HTMLElement {
    return this.root.querySelector('#conteudo') as HTMLElement;
  }
}

customElements.define('erp-shell', ErpShell);

declare global {
  interface HTMLElementTagNameMap {
    'erp-shell': ErpShell;
  }
}
